import React from "react";
import { Document, Page, View, Text, Image, StyleSheet, pdf } from "@react-pdf/renderer";

/** Paper sizes in millimeters (width × height, portrait). */
export const PAPER_SIZES = {
  A4: { width: 210, height: 297, label: "A4" },
  A3: { width: 297, height: 420, label: "A3" },
  Letter: { width: 215.9, height: 279.4, label: "Letter" },
  Legal: { width: 215.9, height: 355.6, label: "Legal" },
};

export const DEFAULT_PDF_OPTIONS = {
  paperSize: "A4",
  orientation: "portrait",
  /** Outer cell width in mm — distance between consecutive vertical cut lines. */
  cellWidth: 32,
  /** Outer cell height in mm — distance between consecutive horizontal cut lines. */
  cellHeight: 36,
  pageMargin: 0,
  /** Inset inside each cell only — does NOT change outer size / line spacing. */
  cellPadding: 3,
  qrSize: null,
  fontSize: 8,
  includeLogo: true,
  showBorders: true,
};

/** Hairline cut-guide thickness in PDF points. */
const CUT_LINE_PT = 0.5;

/** mm → PDF points (1/72 inch). */
export function mmToPt(mm) {
  return (Number(mm) * 72) / 25.4;
}

export function ptToMm(pt) {
  return (Number(pt) * 25.4) / 72;
}

export function getPageDimensions(paperSizeKey, orientation = "portrait") {
  const paper = PAPER_SIZES[paperSizeKey] || PAPER_SIZES.A4;
  if (orientation === "landscape") {
    return { width: paper.height, height: paper.width };
  }
  return { width: paper.width, height: paper.height };
}

function isAutoQrSize(value) {
  return (
    value === null || value === undefined || value === "" || value === "auto"
  );
}

function formatMm(n) {
  return Number.isInteger(n) ? String(n) : String(+Number(n).toFixed(2));
}

/**
 * Outer cell size is EXACTLY cellWidth × cellHeight mm (line-to-line).
 * Never stretched to fill leftover space on the page.
 */
export function computeGridLayout(options = {}) {
  const {
    paperSize = DEFAULT_PDF_OPTIONS.paperSize,
    orientation = DEFAULT_PDF_OPTIONS.orientation,
    cellWidth: rawW = DEFAULT_PDF_OPTIONS.cellWidth,
    cellHeight: rawH = DEFAULT_PDF_OPTIONS.cellHeight,
    pageMargin = DEFAULT_PDF_OPTIONS.pageMargin,
    cellPadding = DEFAULT_PDF_OPTIONS.cellPadding,
    qrSize: desiredQrSize = DEFAULT_PDF_OPTIONS.qrSize,
    fontSize: desiredFontSize = DEFAULT_PDF_OPTIONS.fontSize,
    pageWidth: pageWidthOverride,
    pageHeight: pageHeightOverride,
  } = options;

  const margin = Math.max(0, Number(pageMargin) || 0);
  let padding = Math.max(0, Number(cellPadding) || 0);
  const fontSize = Math.max(4, Math.min(24, Number(desiredFontSize) || 8));

  let cellWidth = Number(rawW);
  let cellHeight = Number(rawH);
  if (!Number.isFinite(cellWidth) || cellWidth <= 0) {
    cellWidth = DEFAULT_PDF_OPTIONS.cellWidth;
  }
  if (!Number.isFinite(cellHeight) || cellHeight <= 0) {
    cellHeight = DEFAULT_PDF_OPTIONS.cellHeight;
  }

  const dims =
    pageWidthOverride != null && pageHeightOverride != null
      ? { width: Number(pageWidthOverride), height: Number(pageHeightOverride) }
      : getPageDimensions(paperSize, orientation);

  const pageWidth = dims.width;
  const pageHeight = dims.height;
  const usableWidth = Math.max(0, pageWidth - margin * 2);
  const usableHeight = Math.max(0, pageHeight - margin * 2);

  const wasClamped =
    cellWidth > usableWidth + 1e-9 || cellHeight > usableHeight + 1e-9;
  if (wasClamped) {
    cellWidth = Math.min(cellWidth, Math.max(usableWidth, 1));
    cellHeight = Math.min(cellHeight, Math.max(usableHeight, 1));
  }

  const maxPad = Math.max(0, Math.min(cellWidth, cellHeight) / 2 - 0.5);
  padding = Math.min(padding, maxPad);

  const columns = Math.max(1, Math.floor(usableWidth / cellWidth + 1e-9));
  const rows = Math.max(1, Math.floor(usableHeight / cellHeight + 1e-9));

  const labelHeight = Math.max(2.5, fontSize * 0.3528 * 1.1);
  const gapQrToLabel = 0.8;
  const innerW = Math.max(1, cellWidth - padding * 2);
  const innerH = Math.max(1, cellHeight - padding * 2);
  const maxQr = Math.max(
    1,
    Math.min(innerW, innerH - labelHeight - gapQrToLabel),
  );

  const qrSize = isAutoQrSize(desiredQrSize)
    ? maxQr
    : Math.min(Math.max(1, Number(desiredQrSize) || 1), maxQr);

  return {
    pageWidth,
    pageHeight,
    cellWidth,
    cellHeight,
    cellClamped: wasClamped,
    qrSize,
    maxQrSize: maxQr,
    qrAuto: isAutoQrSize(desiredQrSize),
    labelHeight,
    gapQrToLabel,
    fontSize,
    columns,
    rows,
    pageMargin: margin,
    cellPadding: padding,
    perPage: columns * rows,
    gridWidth: columns * cellWidth,
    gridHeight: rows * cellHeight,
  };
}

export const convertQRToDataUrl = async (qrElement) => {
  if (!qrElement) return null;
  const canvas = qrElement.querySelector("canvas");
  if (!canvas) return null;
  try {
    return canvas.toDataURL("image/png");
  } catch (error) {
    console.error("Error converting QR canvas to data URL:", error);
    return null;
  }
};

function chunkItems(items, perPage) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return [[]];
  const pages = [];
  for (let i = 0; i < list.length; i += perPage) {
    pages.push(list.slice(i, i + perPage));
  }
  return pages;
}

function CutLine({ x, y, width, height }) {
  return (
    <View
      style={{
        position: "absolute",
        left: x,
        top: y,
        width,
        height,
        backgroundColor: "#000000",
      }}
    />
  );
}

/**
 * React-PDF document: each cell is exactly cellWidth × cellHeight mm (points).
 * Cut lines sit on cell edges so line-to-line = outer cell size.
 */
function QrSheetDocument({ pages, layout, showBorders }) {
  const {
    pageWidth,
    pageHeight,
    pageMargin,
    cellWidth,
    cellHeight,
    cellPadding,
    qrSize,
    gapQrToLabel,
    fontSize,
    columns,
    rows,
    gridWidth,
    gridHeight,
  } = layout;

  const pageWpt = mmToPt(pageWidth);
  const pageHpt = mmToPt(pageHeight);
  const marginPt = mmToPt(pageMargin);
  const cellWpt = mmToPt(cellWidth);
  const cellHpt = mmToPt(cellHeight);
  const padPt = mmToPt(cellPadding);
  const qrPt = mmToPt(qrSize);
  const gapPt = mmToPt(gapQrToLabel);
  const gridWpt = mmToPt(gridWidth);
  const gridHpt = mmToPt(gridHeight);

  const styles = StyleSheet.create({
    page: {
      backgroundColor: "#ffffff",
    },
    grid: {
      position: "absolute",
      left: marginPt,
      top: marginPt,
      width: gridWpt,
      height: gridHpt,
    },
    cell: {
      position: "absolute",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: cellWpt,
      height: cellHpt,
      paddingTop: padPt,
      paddingHorizontal: padPt,
    },
    qrImage: {
      width: qrPt,
      height: qrPt,
    },
    label: {
      marginTop: gapPt,
      fontSize,
      fontFamily: "Helvetica-Bold",
      textAlign: "center",
      maxWidth: Math.max(1, cellWpt - padPt * 2),
    },
    caption: {
      position: "absolute",
      left: marginPt,
      top: marginPt + gridHpt + mmToPt(2),
      fontSize: 7,
      fontFamily: "Helvetica",
      color: "#333333",
    },
  });

  const leftoverBelow = pageHeight - (pageMargin + gridHeight);
  const showCaption = leftoverBelow > 6;

  const cutGuides = showBorders ? (
    <>
      {Array.from({ length: columns + 1 }).map((_, c) => (
        <CutLine
          key={`v-${c}`}
          x={c * cellWpt}
          y={0}
          width={CUT_LINE_PT}
          height={gridHpt}
        />
      ))}
      {Array.from({ length: rows + 1 }).map((_, r) => (
        <CutLine
          key={`h-${r}`}
          x={0}
          y={r * cellHpt}
          width={gridWpt}
          height={CUT_LINE_PT}
        />
      ))}
    </>
  ) : null;

  return (
    <Document
      title={`QR sheet ${formatMm(cellWidth)}x${formatMm(cellHeight)}mm`}
      author="ClickOrder"
    >
      {pages.map((pageItems, pageIndex) => (
        <Page
          key={`page-${pageIndex}`}
          size={{ width: pageWpt, height: pageHpt }}
          style={styles.page}
          wrap={false}
        >
          <View style={{ width: pageWpt, height: pageHpt, position: "relative" }}>
          <View style={styles.grid}>
            {Array.from({ length: columns * rows }).map((_, indexOnPage) => {
              const col = indexOnPage % columns;
              const row = Math.floor(indexOnPage / columns);
              const item = pageItems[indexOnPage];
              const name = (item?.name || "").toString();

              return (
                <View
                  key={`cell-${pageIndex}-${indexOnPage}`}
                  style={[
                    styles.cell,
                    {
                      left: col * cellWpt,
                      top: row * cellHpt,
                    },
                  ]}
                >
                  {item?.dataUrl ? (
                    <Image src={item.dataUrl} style={styles.qrImage} />
                  ) : null}
                  {item ? <Text style={styles.label}>{name}</Text> : null}
                </View>
              );
            })}
            {cutGuides}
          </View>

          {showCaption ? (
            <Text style={styles.caption}>
              Outer cell {formatMm(cellWidth)} x {formatMm(cellHeight)} mm
              (line to line) — print at 100% / Actual size
            </Text>
          ) : null}
          </View>
        </Page>
      ))}
    </Document>
  );
}

/**
 * Generate PDF via @react-pdf/renderer.
 * Outer cell size is exactly options.cellWidth × cellHeight mm (line-to-line).
 */
export async function generateQrPdf({ items, options = {} }) {
  const opts = { ...DEFAULT_PDF_OPTIONS, ...options };
  const orientation =
    opts.orientation === "landscape" ? "landscape" : "portrait";
  const showBorders = opts.showBorders !== false;

  const paperDims = getPageDimensions(opts.paperSize, orientation);
  const pitchW = Number(opts.cellWidth);
  const pitchH = Number(opts.cellHeight);

  const layout = computeGridLayout({
    ...opts,
    cellWidth: pitchW,
    cellHeight: pitchH,
    cellPadding: Number(opts.cellPadding),
    pageMargin: Number(opts.pageMargin),
    pageWidth: paperDims.width,
    pageHeight: paperDims.height,
  });

  if (
    !layout.cellClamped &&
    (Math.abs(layout.cellWidth - pitchW) > 1e-6 ||
      Math.abs(layout.cellHeight - pitchH) > 1e-6)
  ) {
    throw new Error(
      `Cell size mismatch: wanted ${pitchW}×${pitchH}, got ${layout.cellWidth}×${layout.cellHeight}`,
    );
  }

  const list = Array.isArray(items) ? items : [];
  const pages = chunkItems(list, layout.perPage);
  const pageCount = pages.length;

  const doc = (
    <QrSheetDocument pages={pages} layout={layout} showBorders={showBorders} />
  );

  const blob = await pdf(doc).toBlob();
  return { blob, pageCount, layout };
}
